import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/States";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { fetchContent, BLOG_CATEGORIES } from "@/lib/contentApi";

export default function Blog() {
  useSeo("Blog", "Insights, guides and perspectives on Applied AI, Generative AI, UPI, cloud and careers from ASAI.");
  const [cat, setCat] = useState("All");
  const [items, setItems] = useState(null);
  useEffect(() => { fetchContent("blog", cat).then(setItems).catch(() => setItems([])); }, [cat]);
  const cats = ["All", ...BLOG_CATEGORIES];

  return (
    <div data-testid="page-blog">
      <PageHeader overline="Blog" titleLines={["Insights on", "applied AI & careers"]} description="Articles and guides from the ASAI team." />
      <section className="bg-white py-16 md:py-24">
        <div className="container-asai">
          <div className="mb-8 flex flex-wrap gap-2" data-testid="blog-filters">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${cat === c ? "bg-orange text-white" : "border border-navy/15 text-navy/70 hover:border-orange"}`}>{c}</button>
            ))}
          </div>
          {items === null ? null : items.length === 0 ? (
            <EmptyState title="No posts yet" description="Articles and guides will be published here soon." />
          ) : (
            <StaggerGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
              {items.map((p) => (
                <StaggerItem key={p.id}>
                  <Link to={`/blog/${p.slug}`} data-testid="blog-card" className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white transition-all hover:-translate-y-1 hover:shadow-[0_30px_60px_-40px_rgba(6,15,34,0.4)]">
                    {p.featuredImage && <div className="aspect-[16/10] overflow-hidden"><img src={p.featuredImage} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
                    <div className="flex flex-1 flex-col gap-3 p-6">
                      <span className="text-xs font-semibold uppercase tracking-wide text-royal">{p.category}</span>
                      <h3 className="font-heading text-xl leading-snug text-navy">{p.title}</h3>
                      <p className="line-clamp-3 text-sm text-navy/60">{p.excerpt}</p>
                      <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-medium text-orange">Read more <ArrowUpRight className="h-4 w-4" /></span>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerGroup>
          )}
        </div>
      </section>
    </div>
  );
}
